/**
 * Order status colors — My Accounts Migration 2026 (Figma file hMCqLuiLP7b3VQ8x2aWRl9).
 * Light pills: in-progress (#f0f1df / #55571d), delivered, refunded; solid pills for waiting/error states.
 */

/** Default pill text when a status omits `textColor`. */
export const DEFAULT_ORDER_STATUS_TEXT_COLOR = "#ffffff";

/** Figma stepper / primary actions (e.g. node 9:5131). */
export const ORDER_STATUS_BRAND_COLOR = "#737530";

/** Light status pills from Figma (desktop tags + mobile list). */
export const ORDER_STATUS_LIGHT_TAG = {
  CONFIRMED: { backgroundColor: "#e8f5e9", textColor: "#2e7d32" },
  GIFTING_PARTNER: { backgroundColor: "#f0f0e5", textColor: "#737530" },
  IN_PROGRESS: { backgroundColor: "#f0f1df", textColor: "#55571d" },
  DELIVERED: { backgroundColor: "#e8f5ed", textColor: "#328a58" },
  REFUNDED: { backgroundColor: "#fff3df", textColor: "#a96818" },
  CANCELLED: { backgroundColor: "#ed1c2424", textColor: "#ed1c24" },
  EXPIRED: { backgroundColor: "#fdecec", textColor: "#991B1B" },
  RETURNED: { backgroundColor: "#F1EAFE", textColor: "#6B21A8" },
  LOST_IN_TRANSIT: { backgroundColor: "#EAF2FF", textColor: "#1D4ED8" },
};

const confirmedPill = {
  color: ORDER_STATUS_LIGHT_TAG.CONFIRMED.textColor,
  backgroundColor: ORDER_STATUS_LIGHT_TAG.CONFIRMED.backgroundColor,
  textColor: ORDER_STATUS_LIGHT_TAG.CONFIRMED.textColor,
};

const giftingPartnerPill = {
  color: ORDER_STATUS_LIGHT_TAG.GIFTING_PARTNER.textColor,
  backgroundColor: ORDER_STATUS_LIGHT_TAG.GIFTING_PARTNER.backgroundColor,
  textColor: ORDER_STATUS_LIGHT_TAG.GIFTING_PARTNER.textColor,
};

const inProgressPill = {
  color: ORDER_STATUS_BRAND_COLOR,
  backgroundColor: ORDER_STATUS_LIGHT_TAG.IN_PROGRESS.backgroundColor,
  textColor: ORDER_STATUS_LIGHT_TAG.IN_PROGRESS.textColor,
};

/** Order statuses that use a tag instead of the horizontal stepper on list cards. */
export const ORDER_STEPPER_HIDDEN_STATUSES = [
  "Hold",
  "Cancelled",
  "Returned",
  "Waiting",
  "Expired",
  "Refunded",
  "Delivered",
];

export const ORDER_STATUS = {
  WAITING: {
    label: "Waiting",
    color: "#ED1C24",
    backgroundColor: ORDER_STATUS_LIGHT_TAG.CANCELLED.backgroundColor,
    textColor: ORDER_STATUS_LIGHT_TAG.CANCELLED.textColor,
    index: -1,
  },
  CONFIRMED: { label: "Confirmed", ...confirmedPill, index: 0 },
  CANCELLED: {
    label: "Cancelled",
    color: "#ED1C24",
    backgroundColor: ORDER_STATUS_LIGHT_TAG.CANCELLED.backgroundColor,
    textColor: ORDER_STATUS_LIGHT_TAG.CANCELLED.textColor,
    index: -1,
  },
  REFUNDED: {
    label: "Refunded",
    color: "#E5962A",
    backgroundColor: ORDER_STATUS_LIGHT_TAG.REFUNDED.backgroundColor,
    textColor: ORDER_STATUS_LIGHT_TAG.REFUNDED.textColor,
    index: -1,
  },
  DELIVERY_ATTEMPTED: { label: "Delivery Attempted", ...inProgressPill, index: 4 },
  DISPATCHED: { label: "Dispatched", ...inProgressPill, index: 3 },
  DELIVERED_ON: { label: "Delivered on", ...inProgressPill, index: 4 },
  DELIVERED: {
    label: "Delivered",
    color: "#29852F",
    backgroundColor: ORDER_STATUS_LIGHT_TAG.DELIVERED.backgroundColor,
    textColor: ORDER_STATUS_LIGHT_TAG.DELIVERED.textColor,
    index: 4,
  },
  LOST_IN_TRANSIT: {
    label: "Lost In Transit",
    color: "#ED1C24",
    backgroundColor: ORDER_STATUS_LIGHT_TAG.LOST_IN_TRANSIT.backgroundColor,
    textColor: ORDER_STATUS_LIGHT_TAG.LOST_IN_TRANSIT.textColor,
    index: 3,
  },
  RETURNED: {
    label: "Returned",
    color: "#F1EAFE",
    textColor: ORDER_STATUS_LIGHT_TAG.RETURNED.textColor,
    backgroundColor: ORDER_STATUS_LIGHT_TAG.RETURNED.backgroundColor,
    index: -1,
  },
  HOLD: {
    label: "Hold",
    color: "#ED1C24",
    backgroundColor: ORDER_STATUS_LIGHT_TAG.CANCELLED.backgroundColor,
    textColor: ORDER_STATUS_LIGHT_TAG.CANCELLED.textColor,
    index: -1,
  },
  HELD: {
    label: "Held",
    color: "#E5962A",
    backgroundColor: ORDER_STATUS_LIGHT_TAG.REFUNDED.backgroundColor,
    textColor: ORDER_STATUS_LIGHT_TAG.REFUNDED.textColor,
    index: -1,
  },
  EXPIRED: {
    label: "Expired",
    color: "#FDECEC",
    textColor: ORDER_STATUS_LIGHT_TAG.EXPIRED.textColor,
    backgroundColor: ORDER_STATUS_LIGHT_TAG.EXPIRED.backgroundColor,
    index: -1,
  },
  READY_TO_BE_SHIPPED: { label: "Ready to be Shipped", ...inProgressPill, index: 3 },
  SHIPPED: { label: "Shipped", ...inProgressPill, index: 3 },
  OUT_FOR_DELIVERY: { label: "Out for delivery", ...inProgressPill, index: 3 },
  GIFTING_PARTNER_ASSIGNED: { label: "Gifting Partner Assigned", ...giftingPartnerPill, index: 1 },
  PROCESSING: { label: "Processing", ...inProgressPill, index: 2 },
};

/** @param {string} statusLabel */
export function findOrderStatusConfig(statusLabel) {
  if (!statusLabel) {
    return undefined;
  }
  const normalized = String(statusLabel).trim().toLowerCase();
  return Object.values(ORDER_STATUS).find((entry) => entry.label.toLowerCase() === normalized);
}
